/**
 * 폼 입력 검증 규칙 모음.
 *
 * 모든 항목은 아래 세 가지를 같은 형태로 내보낸다.
 * - `<FIELD>_MESSAGE` : 형식 오류 메시지. placeholder 등 화면 문구로도 쓴다.
 * - `is<Field>Valid`  : 값 하나를 직접 검사하는 함수. alert 같은 명령형 검사에 쓴다.
 * - `<field>Rules`    : react-hook-form 의 `rules` 에 그대로 넘기는 규칙 팩토리.
 *
 * 규칙 팩토리는 모두 {@link FieldRuleOptions} 하나만 받고, 검사는 항상 `validate` 로
 * 수행한다. `pattern` / `minLength` 같은 규칙을 섞어 쓰지 않으므로 전화번호(하이픈 제거)나
 * 생일(미완성 값)처럼 정규식만으로 안 되는 항목도 같은 방식으로 다룰 수 있다.
 */

/** 규칙 팩토리 공통 옵션. 모든 `<field>Rules` 가 이 형태만 받는다. */
export type FieldRuleOptions<TValue = any> = {
  /**
   * 필수 입력 여부.
   * `true` 면 `label` 로 만든 기본 메시지를, 문자열이면 그 문자열을 메시지로 쓴다.
   * 생략하면 값이 비어 있어도 통과시킨다.
   */
  required?: boolean | string;
  /** 메시지에 넣을 항목명. 생략하면 항목별 기본 이름을 쓴다. (예: '보호자 이름') */
  label?: string;
  /** 화면마다 다른 추가 검사. 형식 검사(`format`) 와 함께 실행된다. */
  validate?: Record<string, (value: TValue) => boolean | string>;
};

/**
 * 항목 하나의 검증 방법 정의. {@link createRules} 에만 쓴다.
 */
type FieldRuleSpec<TValue> = {
  /** `label` 을 넘기지 않았을 때 메시지에 쓰는 항목명. */
  defaultLabel: string;
  /** 형식 검사. 값이 비어 있는 경우는 `required` 가 맡으므로 여기까지 오지 않는다. */
  isValid: (value: TValue) => boolean;
  /** 형식 오류 메시지. 값에 따라 메시지가 갈리는 항목은 `value` 를 함께 본다. */
  message: (label: string, value: TValue) => string;
};

/** 한글 조사. 앞말의 받침 유무로 고른다. */
const josa = (word: string, [withFinal, withoutFinal]: [string, string]) => {
  const code = word.charCodeAt(word.length - 1);
  const isHangul = code >= 0xac00 && code <= 0xd7a3;
  const hasFinalConsonant = isHangul && (code - 0xac00) % 28 !== 0;
  return `${word}${hasFinalConsonant ? withFinal : withoutFinal}`;
};

const objectParticle = (word: string) => josa(word, ['을', '를']);
const topicParticle = (word: string) => josa(word, ['은', '는']);

/** 공백만 있는 값도 비어 있는 것으로 본다. */
const isEmpty = (value: unknown) =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && !value);

/**
 * 항목 정의로부터 규칙 팩토리를 만든다.
 * 만들어진 팩토리는 `{ required?, validate: { format, ...추가검사 } }` 를 돌려준다.
 */
const createRules =
  <TValue>({ defaultLabel, isValid, message }: FieldRuleSpec<TValue>) =>
  ({
    required,
    label = defaultLabel,
    validate,
  }: FieldRuleOptions<TValue> = {}) => ({
    ...(required
      ? {
          required:
            typeof required === 'string'
              ? required
              : `${objectParticle(label)} 입력해주세요`,
        }
      : {}),
    validate: {
      format: (value: TValue) =>
        isEmpty(value) || isValid(value) || message(label, value),
      ...validate,
    },
  });

//===============================================================================================================
// 비밀번호

/** 영문 / 숫자 / 특수문자(!@#$%^&*)를 각각 1자 이상 포함한 8~20자. */
export const PASSWORD_PATTERN =
  /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,20}$/;

export const PASSWORD_MESSAGE =
  '영문, 숫자, 특수문자를 포함하여 8~20자로 입력해주세요. 특수문자는 !@#$%^&* 만 사용 가능합니다.';

export const isValidPassword = (value?: string) =>
  PASSWORD_PATTERN.test(value ?? '');

export const passwordRules = createRules<string | undefined>({
  defaultLabel: '비밀번호',
  isValid: isValidPassword,
  message: () => PASSWORD_MESSAGE,
});

//===============================================================================================================
// 이메일

export const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const EMAIL_MESSAGE = '이메일 형식이 아닙니다.';

export const isValidEmail = (value?: string) => EMAIL_PATTERN.test(value ?? '');

export const emailRules = createRules<string | undefined>({
  defaultLabel: '이메일',
  isValid: isValidEmail,
  message: () => EMAIL_MESSAGE,
});

//===============================================================================================================
// 아이디

/**
 * 아이디: 영문자로 시작하는 영문 / 숫자 / _ 조합 5~20자, 또는 이메일 형식.
 * 이메일 쪽은 {@link EMAIL_PATTERN} 을 이어붙여 두 규칙이 어긋나지 않게 한다.
 */
export const UID_PATTERN = new RegExp(
  `^[a-zA-Z][a-zA-Z0-9_]{4,19}$|${EMAIL_PATTERN.source}`,
);

/**
 * 일반 아이디의 길이 제한. {@link UID_PATTERN} 이 이미 강제하는 값이라,
 * 여기서는 '형식이 틀렸다' 대신 길이 오류 메시지를 따로 내주는 데만 쓴다.
 * 이메일 형식 아이디에는 적용하지 않는다.
 */
export const UID_MIN_LENGTH = 5;
export const UID_MAX_LENGTH = 20;

export const UID_MESSAGE =
  '아이디는 영문자로 시작해야 하며, 영문자와 숫자, _로 구성될 수 있습니다. 이외 특수문자는 사용할 수 없습니다. 또는 이메일 형식이어야 합니다.';

/** 아이디 형식 검사. 길이는 보지 않는다. 길이까지 함께 보려면 {@link uidRules} 를 쓴다. */
export const isValidUid = (value?: string) => UID_PATTERN.test(value ?? '');

/**
 * 검사는 {@link UID_PATTERN} 하나로 하고, 길이 오류일 때만 메시지를 갈라준다.
 * 중복확인처럼 화면마다 다른 검사는 `validate` 옵션으로 덧붙인다.
 */
export const uidRules = createRules<string | undefined>({
  defaultLabel: '아이디',
  isValid: isValidUid,
  message: (label, value) => {
    const uid = value ?? '';
    // 이메일 형식 아이디는 길이 제한이 없으므로 형식 메시지만 낸다.
    if (uid.includes('@')) return UID_MESSAGE;
    if (uid.length < UID_MIN_LENGTH)
      return `${topicParticle(label)} ${UID_MIN_LENGTH}자 이상으로 입력해주세요.`;
    if (uid.length > UID_MAX_LENGTH)
      return `${topicParticle(label)} ${UID_MAX_LENGTH}자 이하로 입력해주세요.`;
    return UID_MESSAGE;
  },
});

//===============================================================================================================
// 전화번호

/**
 * 전화번호: 하이픈을 제외한 숫자 기준. 휴대폰과 지역번호(02 포함)를 모두 허용한다.
 */
export const PHONE_PATTERN = /^0(?:2|[0-9]{2})(?:[0-9]{3,4})[0-9]{4}$/;

export const PHONE_MESSAGE = '올바른 전화번호를 입력하세요 (예: 01012345678)';

/**
 * 전화번호 입력값에서 숫자만 남긴다.
 * 하이픈은 `phoneFormat` 으로 화면에만 붙이고, 폼 값과 서버 전송값은 항상 숫자만 유지한다.
 */
export const normalizePhone = (value: string) => value.replace(/[^0-9]/g, '');

/** 하이픈을 제거한 뒤 전화번호 형식을 검사한다. */
export const isValidPhone = (value?: string) =>
  PHONE_PATTERN.test(normalizePhone(value ?? ''));

export const phoneRules = createRules<string | undefined>({
  defaultLabel: '전화번호',
  isValid: isValidPhone,
  message: () => PHONE_MESSAGE,
});

//===============================================================================================================
// 생년월일

export const BIRTHDAY_MESSAGE = '연도, 월, 일을 모두 입력해주세요';

/** 완전한 생일 값(YYYY-MM-DD)인지 확인한다. */
export const isValidBirthday = (value?: string | Date) => {
  if (!value) return false;
  if (value instanceof Date) return true;
  return /^\d{4}-\d{2}-\d{2}/.test(value);
};

/**
 * `BirthdayInput` 은 연/월만 고른 미완성 값('1990-01-')도 그대로 올려보내므로 여기서 막는다.
 */
export const birthdayRules = createRules<string | Date | undefined>({
  defaultLabel: '생년월일',
  isValid: isValidBirthday,
  message: () => BIRTHDAY_MESSAGE,
});

//===============================================================================================================
// 이름

/** 이름 최대 길이. 앞뒤 공백을 제외한 글자 수 기준. */
export const NAME_MAX_LENGTH = 20;

/**
 * 완성되지 않은 한글 낱자(ㄱ, ㅏ, ㅘ 등).
 * 호환 자모(U+3131~U+318E)와 한글 자모(U+1100~U+11FF), 확장 자모 영역을 모두 본다.
 */
const HANGUL_JAMO_PATTERN = /[ᄀ-ᇿ㄰-㆏ꥠ-꥿ힰ-퟿]/;

export const NAME_JAMO_MESSAGE = '자음이나 모음만으로는 입력할 수 없습니다';

/** 자음 / 모음 낱자가 섞여 있는지 확인한다. ('ㄱㄴ', '김ㅁ' 모두 해당) */
const hasHangulJamo = (value: string) => HANGUL_JAMO_PATTERN.test(value);

/**
 * 이름 형식 검사: 앞뒤 공백을 제외하고 1자 이상 {@link NAME_MAX_LENGTH} 자 이하.
 * 문자 종류는 제한하지 않는다. 외국인 이름이나 '남궁 민수' 처럼 중간에 공백이 들어가는
 * 이름까지 받기 위한 것으로, 공백만 입력한 값과 길이 초과,
 * 그리고 완성되지 않은 한글 낱자만 걸러낸다.
 */
export const isValidName = (value?: string) => {
  const name = (value ?? '').trim();
  return (
    name.length > 0 && name.length <= NAME_MAX_LENGTH && !hasHangulJamo(name)
  );
};

/** 공백만 입력한 값은 `required` 를 통과해버리므로 형식 검사에서 함께 막는다. */
export const nameRules = createRules<string | undefined>({
  defaultLabel: '이름',
  isValid: isValidName,
  message: (label, value) => {
    const name = (value ?? '').trim();
    if (!name) return `${objectParticle(label)} 입력해주세요`;
    if (hasHangulJamo(name))
      return `${topicParticle(label)} ${NAME_JAMO_MESSAGE}`;
    return `${topicParticle(label)} ${NAME_MAX_LENGTH}자 이내로 입력해주세요`;
  },
});

//===============================================================================================================
// 영어 이름

/**
 * 영어 이름: 영문 대문자 2~20자.
 * 단어 사이 공백은 허용하되 앞뒤 공백은 허용하지 않는다.
 */
export const ENGLISH_NAME_PATTERN = /^[A-Z][A-Z ]{0,18}[A-Z]$/;

export const ENGLISH_NAME_MESSAGE =
  '2자 이상 20자 이내의 영문 대문자, 공백만 입력해주세요';

export const isValidEnglishName = (value?: string) =>
  ENGLISH_NAME_PATTERN.test(value ?? '');

export const englishNameRules = createRules<string | undefined>({
  defaultLabel: '영어이름',
  isValid: isValidEnglishName,
  message: () => ENGLISH_NAME_MESSAGE,
});

//===============================================================================================================
// 반 이름

/** 반 이름 최대 길이. 앞뒤 공백을 제외한 글자 수 기준. */
export const CLASS_GROUP_NAME_MAX_LENGTH = 40;

export const CLASS_GROUP_NAME_MESSAGE = `${CLASS_GROUP_NAME_MAX_LENGTH}자 이내로 입력해주세요`;

export const CLASS_GROUP_NAME_PLACEHOLDER = `${CLASS_GROUP_NAME_MAX_LENGTH}자 이내로 입력해주세요 (필수)`;

/**
 * 반 이름 형식 검사: 앞뒤 공백을 제외하고 1자 이상 {@link CLASS_GROUP_NAME_MAX_LENGTH} 자 이하.
 * 문자 종류는 제한하지 않는다. '초3 A반 (월/수)' 처럼 특수문자와 공백을 섞어 부르는 이름이
 * 많아서, 공백만 입력한 값과 길이 초과만 걸러낸다.
 */
export const isValidClassGroupName = (value?: string) => {
  const name = (value ?? '').trim();
  return name.length > 0 && name.length <= CLASS_GROUP_NAME_MAX_LENGTH;
};

/** 공백만 입력한 값은 `required` 를 통과해버리므로 형식 검사에서 함께 막는다. */
export const classGroupNameRules = createRules<string | undefined>({
  defaultLabel: '반 이름',
  isValid: isValidClassGroupName,
  message: (label, value) => {
    if (!(value ?? '').trim()) return `${objectParticle(label)} 입력해주세요`;
    return `${topicParticle(label)} ${CLASS_GROUP_NAME_MESSAGE}`;
  },
});

//===============================================================================================================
// 반 설명

/** 반 설명 최대 길이. 앞뒤 공백을 제외한 글자 수 기준. */
export const CLASS_GROUP_DESCRIPTION_MAX_LENGTH = 200;

export const CLASS_GROUP_DESCRIPTION_MESSAGE = `${CLASS_GROUP_DESCRIPTION_MAX_LENGTH}자 이내로 입력해주세요`;

export const CLASS_GROUP_DESCRIPTION_PLACEHOLDER =
  '반 설명을 입력해주세요 (선택, 200자 이내)';

/**
 * 반 설명 형식 검사: 앞뒤 공백을 제외하고 {@link CLASS_GROUP_DESCRIPTION_MAX_LENGTH} 자 이하.
 * 설명은 선택 항목이라 비어 있어도 통과한다.
 */
export const isValidClassGroupDescription = (value?: string) => {
  return (value ?? '').trim().length <= CLASS_GROUP_DESCRIPTION_MAX_LENGTH;
};

export const classGroupDescriptionRules = createRules<string | undefined>({
  defaultLabel: '반 설명',
  isValid: isValidClassGroupDescription,
  message: (label) =>
    `${topicParticle(label)} ${CLASS_GROUP_DESCRIPTION_MESSAGE}`,
});
